import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button, Input, Card } from "../components/ui";
import Footer from "../components/Footer";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { apiError } from "../lib/api";

export default function AuthPage({ mode = "login" }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, loginWithGoogle, register } = useAuth();
  const [googleReady, setGoogleReady] = useState(false);
  const isLogin = mode === "login";

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    mobile: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email.trim(), form.password);
      } else {
        await register({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          mobile: form.mobile.replace(/[\s\-().]/g, ""),
        });
      }
      toast.success(isLogin ? "Welcome back!" : "Account created");
      goNext();
    } catch (err) {
      toast.error(apiError(err, "Auth failed"));
    } finally {
      setLoading(false);
    }
  };

  const goNext = () => {
    const dest = loc.state?.from || new URLSearchParams(loc.search).get("from") || "/home";
    nav(dest, { replace: true });
  };

  const google = async (credential) => {
    setLoading(true);
    try {
      const u = await loginWithGoogle(credential);
      toast.success(u.is_new ? "Account created — let's get you into a club" : `Welcome back, ${u.name?.split(" ")[0] || "skipper"}!`);
      goNext(); // new accounts are sent on to create / join a club
    } catch (err) {
      toast.error(apiError(err, "Google sign-in failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell flex flex-col">
      <div className="flex-1">
        {/* Centered Logo Section */}
        <div className="flex justify-center pt-12 pb-8">
          <div className="flex flex-col items-center">
            <img 
              src="/logo.png" 
              alt="Cricso" 
              className="h-32 w-32 object-contain"
            />
          </div>
        </div>

        <div className="px-6 pb-6">
        <h1 className="text-3xl font-bold leading-tight text-center">
          {isLogin ? (
            <>Welcome back, <span className="text-brand">skipper.</span></>
          ) : (
            <>Run your club<br/>from your <span className="text-brand">pocket.</span></>
          )}
        </h1>
        <p className="text-ink-muted mt-2 text-[15px] text-center">
          {isLogin
            ? "Sign in to manage matches, expenses & your team's ledger."
            : "One place for matches, players and every rupee — split fairly."}
        </p>
      </div>

      <Card className="mx-5 mb-5">
        <GoogleSignInButton mode={isLogin ? "signin" : "signup"} onCredential={google} onReady={setGoogleReady} />
        {googleReady && (
          <div className="flex items-center gap-3 my-4 text-xs text-ink-muted uppercase tracking-widest font-semibold">
            <span className="flex-1 h-px bg-black/10" />
            or with email
            <span className="flex-1 h-px bg-black/10" />
          </div>
        )}
        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          {!isLogin && (
            <Input
              label="Your Name"
              placeholder="Virat K."
              value={form.name}
              onChange={update("name")}
              required
              data-testid="auth-name-input"
            />
          )}
          <Input
            label="Email"
            type="email"
            placeholder="you@club.in"
            value={form.email}
            onChange={update("email")}
            required
            data-testid="auth-email-input"
          />
          <Input
            label="Password"
            type="password"
            placeholder="At least 6 characters"
            value={form.password}
            onChange={update("password")}
            required
            data-testid="auth-password-input"
            minLength={6}
          />
          {isLogin && (
            <div className="text-right -mt-2">
              <Link
                to="/forgot-password"
                state={{ email: form.email.trim() }}
                className="text-sm text-brand font-semibold"
                data-testid="auth-forgot-password"
              >
                Forgot password?
              </Link>
            </div>
          )}
          {!isLogin && (
            <div>
              <Input
                label="Mobile number (with country code) *"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+918888888888"
                value={form.mobile}
                onChange={update("mobile")}
                required
                pattern="^\+[1-9][0-9\s\-]{7,19}$"
                title="Start with + and your country code, e.g. +918888888888"
                data-testid="auth-mobile-input"
              />
              <p className="text-xs text-ink-muted mt-1.5">
                Start with <b>+</b> and your country code, e.g. <b>+918888888888</b>. Your club uses it for WhatsApp.
              </p>
            </div>
          )}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={loading}
            data-testid="auth-submit-btn"
          >
            {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </Button>
        </form>
      </Card>

      <div className="text-center text-sm text-ink-muted mt-2 pb-10">
        {isLogin ? (
          <>New here?{" "}
            <Link to="/register" className="text-brand font-semibold" data-testid="auth-switch-register">
              Create account
            </Link>
          </>
        ) : (
          <>Already have an account?{" "}
            <Link to="/login" className="text-brand font-semibold" data-testid="auth-switch-login">
              Sign in
            </Link>
          </>
        )}
      </div>
      </div>
      <Footer />
    </div>
  );
}
